import React, { useCallback, useEffect, useRef, useState } from "react";
import Typography from "@material-ui/core/Typography";
import { IconButton, Tooltip, Slide } from "@material-ui/core";
import { makeStyles, withStyles, createStyles } from "@material-ui/core/styles";
import ArrowLeftIcon from "./icons/ArrowLeftIcon";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "./icons/SearchIcon";
import Divider from "@material-ui/core/Divider";
import AddRoundedIcon from "@material-ui/icons/AddRounded";
import { useGetWorkflowVariables } from "./hooks/useGetWorkflowVariables";
import FormColumn from "./components/FormColumn";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import DeleteRoundedIcon from "@material-ui/icons/DeleteRounded";
import EmptyStateCreateDataTable from "./components/EmptyStateCreateDataTable";
import KeyboardArrowLeftRoundedIcon from "@material-ui/icons/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@material-ui/icons/KeyboardArrowRightRounded";
import NoWorkflowConfigured from "./components/NoWorkflowConfigured";
import TableHeaderCellName from "./components/TableHeaderCellName";
import DataTableDialogWrapper from "../DataTableDialogWrapper";
import { useAppsummaryConfigurationMutation } from "./hooks/useAppsummaryConfigurationMutation";
import { errorToastify, successToastify } from "../../utils/Toastify";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { variableGroups } from "../../../EditorLayout/Pages/Workflow/components/utils/constants";
import { useDispatch } from "react-redux";
import { rSelectApp } from "../../../../store/actions/properties";

const useStyles = makeStyles(() =>
  createStyles({
    customPadding: {
      padding: "32px 24px 20px",
    },
    leftRightPadding: {
      padding: "0 24px",
    },
    customRemoveIcon: {
      color: "#DE5439",
    },
    arrowPagination: {
      color: "#DE5439",
      background: "#fbeae7",
      padding: "2px",
      borderRadius: "5px",
    },
    reuseDisplayFlex: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    saveButton: {
      background: "#292929",
      color: "#ffffff",
      fontFamily: "Avenir",
      fontSize: "14px",
      textTransform: "none",
      padding: "8px 50px",
      borderRadius: "8px",
    },
    table: {
      borderCollapse: "separate",
    },
    tableHead: {
      background: "#FFFFFF",
      border: "1px solid #F0F0F0",

      // Target all th elements inside thead
      "& th": {
        border: "1px solid #F0F0F0",
        borderRight: "0px",
        borderLeft: "0px",
      },

      // First th element
      "& th:first-of-type": {
        borderTopLeftRadius: "8px",
        borderBottomLeftRadius: "8px",
        borderLeft: "1px solid #F0F0F0",
      },

      // Last th element
      "& th:last-of-type": {
        borderTopRightRadius: "8px",
        borderBottomRightRadius: "8px",
        borderRight: "1px solid #F0F0F0",
      },
    },
    tableCell: {
      borderBottom: "none",
      color: "#858585",
      fontSize: "14px !important",
      fontFamily: "Avenir",
      fontWeight: 400,
    },
    fontSizeLarge: {
      fontSize: "20px",
    },
  })
);

const CustomSearchTextField = withStyles({
  root: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f0f0f0",
      borderRadius: "100px",
      "& fieldset": {
        outline: "none",
        border: 0,
      },
    },
    "& .MuiOutlinedInput-input": {
      "&::placeholder": {
        color: "#535353",
        fontWeight: 400,
        fontSize: "14px",
        font: "Avenir",
      },
    },
  },
})(TextField);

const StyledTableCell = withStyles((theme) => ({
  head: {
    fontFamily: "Avenir",
    fontWeight: 500,
    fontSize: "14px !important",
    color: "#535353",
    padding: "5px !important",
    background: "#FFFFFF",
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

const CreateDataTable = ({
  open,
  handleClose,
  appId,
  appName,
  appSummaryConfiguration,
}) => {
  const dispatch = useDispatch();

  const classes = useStyles();

  const [addColumns, setAddColumns] = useState(false);
  const [selectedFormColumns, setSelectedFormColumns] = useState(
    appSummaryConfiguration
  );
  const [filteredWorkFlowVariables, setFilteredWorkFlowVariables] = useState(
    []
  );
  const [searchFormItem, setSearchFormItem] = useState("");

  const saveButtonRef = useRef(true);

  const onGetWorkflowVariablesSuccess = (data) => {
    setFilteredWorkFlowVariables(data?.data?.[0]?.variables);
  };

  const {
    data: workflowDataVariables,
    isError: isWorkflowDataVariablesError,
    isFetching: isFetchingWorkflowDataVariables,
  } = useGetWorkflowVariables(appId, onGetWorkflowVariablesSuccess);

  const {
    data: updatedAppSummaryConfigurationData,
    mutate,
    isLoading: isSavingAppSummaryConfiguration,
  } = useAppsummaryConfigurationMutation();

  useEffect(() => {
    const variables = workflowDataVariables?.data?.[0]?.variables || [];

    if (searchFormItem === "") {
      setFilteredWorkFlowVariables(variables);
      return;
    }

    const lowerCaseSearchTerm = searchFormItem.toLowerCase();

    const searchResults = variables?.filter((item) =>
      item?.info?.name.toLowerCase().includes(lowerCaseSearchTerm)
    );
    setFilteredWorkFlowVariables(searchResults);
  }, [searchFormItem]);

  const handleSearchChange = (e) => {
    setSearchFormItem(e.target.value);
  };

  const saveDataTableMutation = (isCleared = false) => {
    const message = isCleared
      ? "Cleared Data Table Successfully"
      : "Data Table Updated Successfully";

    mutate(
      {
        appId,
        data: { summaryConfiguration: isCleared ? [] : selectedFormColumns },
      },
      {
        onSuccess: ({ data }) => {
          dispatch(rSelectApp(data?.data)); // update the global state of the selected app

          const summaryConfigurationData = data?.data?.summaryConfiguration;
          setSelectedFormColumns(summaryConfigurationData);
          successToastify(message);
        },
        onError: () => {
          errorToastify("An error occurred please try again");
        },
      }
    );
  };

  const handleCheckAndUnCheckColumn = (item) => {
    const itemId = item;

    setSelectedFormColumns((prevItems) => {
      const itemIdExists = prevItems?.some(
        (prevItem) => prevItem.id === itemId
      );

      if (itemIdExists) {
        return prevItems?.filter((prevItem) => prevItem.id !== itemId);
      } else {
        return [...prevItems, { id: itemId }];
      }
    });

    saveButtonRef.current = false;
  };

  const handleSaveCreateDataTable = (e) => {
    e.preventDefault();
    saveButtonRef.current = true;

    saveDataTableMutation();
  };

  const placeholderRowItemCell = selectedFormColumns?.map((selectedform) => {
    const matchingItem = workflowDataVariables?.data?.[0]?.variables?.find(
      (workflowItem) => workflowItem.id === selectedform.id
    );

    if (matchingItem) {
      return {
        name: `Preview-${matchingItem.info.name}`,
      };
    }

    return {};
  });

  const placeholderPaginationLength = 10;

  const onDragEnd = useCallback(
    (dragResult) => {
      const { destination, source } = dragResult;

      if (
        !destination ||
        (destination.droppableId === source.droppableId &&
          destination.index === source.index)
      ) {
        return;
      }

      const newItems = Array.from(selectedFormColumns);

      const [reorderedItem] = newItems.splice(source.index, 1);
      newItems.splice(destination.index, 0, reorderedItem);

      setSelectedFormColumns(newItems);
      saveButtonRef.current = false;
    },
    [selectedFormColumns]
  );

  const formItemsVariableGroup = [variableGroups.FORM];

  return (
    <DataTableDialogWrapper
      open={open}
      onClose={handleClose}
      title="Create data table"
      description="Select the fields that you want as columns and in their order"
      sidebarMenu={
        <>
          {addColumns ? (
            <Slide direction="left" in={addColumns}>
              <div>
                <div style={{ display: "grid", gap: "25px" }}>
                  <div className={classes.leftRightPadding}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        paddingTop: "32px",
                      }}
                    >
                      <IconButton
                        edge="start"
                        color="inherit"
                        onClick={() => {
                          setAddColumns(false);
                          setSearchFormItem("");
                        }}
                        aria-label="back-column"
                        size="medium"
                      >
                        <ArrowLeftIcon />
                      </IconButton>

                      <Typography
                        style={{
                          fontFamily: " Avenir",
                          fontSize: "20px",
                          fontWeight: 500,
                          color: "#292929",
                          textAlign: "center",
                        }}
                      >
                        Add columns
                      </Typography>
                    </div>

                    <Typography
                      style={{
                        fontFamily: " Avenir",
                        fontSize: "12px",
                        fontWeight: 400,
                        color: "#535353",
                      }}
                    >
                      Select the columns you want to preview
                    </Typography>
                  </div>

                  <div className={classes.leftRightPadding}>
                    <CustomSearchTextField
                      fullWidth
                      placeholder="Search"
                      id="input-with-icon-textfield"
                      size="small"
                      variant="outlined"
                      value={searchFormItem}
                      onChange={handleSearchChange}
                      inputProps={{
                        style: {
                          color: "#535353",
                          font: "Avenir",
                          fontWeight: 400,
                          fontSize: "14px",
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      classes={{
                        root: classes.customTextField,
                      }}
                    />
                  </div>

                  {filteredWorkFlowVariables?.some((formItems) =>
                    formItemsVariableGroup.includes(formItems.info.group)
                  ) && (
                    <>
                      <div
                        className={classes.leftRightPadding}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          style={{
                            fontFamily: "Avenir",
                            fontSize: "14px",
                            fontWeight: 800,
                            color: "#535353",
                          }}
                        >
                          Form columns
                        </Typography>

                        <Typography
                          style={{
                            fontFamily: "Avenir",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#535353",
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                        >
                          <Tooltip
                            placement="bottom"
                            title="Applies only to form items"
                          >
                            <span
                              onClick={() => {
                                const formItemIds = filteredWorkFlowVariables
                                  ?.filter?.(
                                    (variable) =>
                                      formItemsVariableGroup.includes(
                                        variable?.info?.group
                                      ) &&
                                      !selectedFormColumns.some(
                                        (item) => variable?.id === item.id
                                      )
                                  )
                                  ?.map((item) => {
                                    return { id: item.id };
                                  });

                                setSelectedFormColumns((prevItems) => [
                                  ...prevItems,
                                  ...formItemIds,
                                ]);

                                saveButtonRef.current = false;
                              }}
                            >
                              Select all
                            </span>
                          </Tooltip>
                        </Typography>
                      </div>
                      <div
                        className={classes.leftRightPadding}
                        style={{
                          display: "grid",
                          gap: "12px",
                          maxHeight: "260px",
                          overflow: "auto",
                        }}
                      >
                        {filteredWorkFlowVariables
                          ?.filter?.((variable) =>
                            formItemsVariableGroup.includes(variable.info.group)
                          )
                          ?.map((variable) => (
                            <FormColumn
                              key={variable?.id}
                              variableId={variable?.id}
                              isChecked={selectedFormColumns?.some(
                                (col) => col.id === variable?.id
                              )}
                              onColumnClick={handleCheckAndUnCheckColumn}
                              columnName={variable?.info?.name}
                              checkboxStyle={classes.customCheckbox}
                              selectedIconStyle={classes.customRemoveIcon}
                            />
                          ))}
                      </div>
                      <Divider />
                    </>
                  )}

                  <>
                    <div
                      className={classes.leftRightPadding}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        style={{
                          fontFamily: "Avenir",
                          fontSize: "14px",
                          fontWeight: 800,
                          color: "#535353",
                        }}
                      >
                        System variables
                      </Typography>

                      <Typography
                        style={{
                          fontFamily: "Avenir",
                          fontSize: "12px",
                          fontWeight: 400,
                          color: "#535353",
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                      >
                        <Tooltip
                          placement="bottom"
                          title="Applies only to system variables"
                        >
                          <span
                            onClick={() => {
                              const nonformItemIds = filteredWorkFlowVariables
                                ?.filter?.(
                                  (variable) =>
                                    !formItemsVariableGroup?.includes(
                                      variable.info.group
                                    ) &&
                                    !selectedFormColumns?.some(
                                      (item) => variable?.id === item.id
                                    )
                                )
                                ?.map((item) => {
                                  return { id: item.id };
                                });

                              setSelectedFormColumns((prevItems) => [
                                ...prevItems,
                                ...nonformItemIds,
                              ]);

                              saveButtonRef.current = false;
                            }}
                          >
                            Select all
                          </span>
                        </Tooltip>
                      </Typography>
                    </div>
                    <div
                      className={classes.leftRightPadding}
                      style={{
                        display: "grid",
                        gap: "12px",
                        maxHeight: "260px",
                        overflow: "auto",
                      }}
                    >
                      {filteredWorkFlowVariables
                        ?.filter?.(
                          (variable) =>
                            !formItemsVariableGroup.includes(
                              variable.info.group
                            )
                        )
                        ?.map((variable) => (
                          <FormColumn
                            key={variable?.id}
                            variableId={variable?.id}
                            isChecked={selectedFormColumns?.some(
                              (col) => col.id === variable?.id
                            )}
                            onColumnClick={handleCheckAndUnCheckColumn}
                            columnName={variable?.info?.name}
                            checkboxStyle={classes.customCheckbox}
                            selectedIconStyle={classes.customRemoveIcon}
                          />
                        ))}
                    </div>
                  </>

                  {!filteredWorkFlowVariables?.length && (
                    <div>
                      <Typography
                        style={{
                          fontFamily: " Avenir",
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#858585",
                          textAlign: "center",
                          paddingTop: "10px",
                        }}
                      >
                        No Result found.
                      </Typography>
                    </div>
                  )}
                </div>
              </div>
            </Slide>
          ) : (
            <>
              <div
                className={classes.customPadding}
                style={{
                  borderBottom: "1px solid #F0F0F0",
                }}
              >
                <Typography
                  gutterBottom
                  style={{
                    fontFamily: " Avenir",
                    fontSize: "14px",
                    fontWeight: 800,
                    color: "#535353",
                  }}
                >
                  Name
                </Typography>

                <div
                  style={{
                    padding: "8px",
                    background: "#F8F8F8",
                    borderRadius: "8px",
                  }}
                >
                  <Typography
                    style={{
                      fontFamily: " Avenir",
                      fontSize: "12px",
                      fontWeight: 400,
                      color: "#292929",
                    }}
                  >
                    {appName}
                  </Typography>
                </div>
              </div>

              <div className={classes.customPadding}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingBottom: "16px",
                  }}
                >
                  <Typography
                    style={{
                      fontFamily: " Avenir",
                      fontSize: "14px",
                      fontWeight: 800,
                      color: "#535353",
                    }}
                  >
                    Tables
                  </Typography>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    <IconButton
                      edge="start"
                      color="inherit"
                      onClick={() => {
                        setAddColumns(true);
                      }}
                      disabled={isFetchingWorkflowDataVariables}
                      aria-label="add"
                      size="small"
                    >
                      <Tooltip
                        placement="top"
                        title="Add columns to your data table"
                      >
                        <AddRoundedIcon
                          fontSize="large"
                          classes={{ fontSizeLarge: classes.fontSizeLarge }}
                        />
                      </Tooltip>
                    </IconButton>

                    {!!selectedFormColumns?.length && (
                      <IconButton
                        edge="start"
                        color="inherit"
                        onClick={() => {
                          setSelectedFormColumns([]);

                          if (
                            !!appSummaryConfiguration?.length ||
                            !!updatedAppSummaryConfigurationData?.data?.data
                              ?.summaryConfiguration?.length
                          ) {
                            saveDataTableMutation(true);
                          }
                        }}
                        disabled={isFetchingWorkflowDataVariables}
                        aria-label="delete-all"
                        size="small"
                      >
                        <Tooltip
                          placement="top"
                          title="Delete all columns on your data table"
                        >
                          <DeleteRoundedIcon fontSize="medium" />
                        </Tooltip>
                      </IconButton>
                    )}
                  </div>
                </div>

                {!isFetchingWorkflowDataVariables && (
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="droppablecolumnlist">
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "16px",
                            maxHeight: "620px",
                            overflow: "auto",
                          }}
                          {...provided.droppableProps}
                        >
                          {selectedFormColumns
                            ?.map((selectedform) => {
                              const matchingItem =
                                workflowDataVariables?.data?.[0]?.variables?.find(
                                  (workflowItem) =>
                                    workflowItem.id === selectedform.id
                                );

                              if (matchingItem) {
                                return {
                                  id: matchingItem.id,
                                  name: matchingItem.info.name,
                                };
                              }
                              return null;
                            })
                            ?.filter((variable) => variable !== null)
                            ?.map((variable, index) => (
                              <Draggable
                                key={variable?.id}
                                draggableId={variable?.id}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                  >
                                    <FormColumn
                                      key={variable?.id}
                                      dragHandle={{
                                        ...provided.dragHandleProps,
                                      }}
                                      variableId={variable?.id}
                                      isSelected={selectedFormColumns?.some(
                                        (col) => col.id === variable?.id
                                      )}
                                      onColumnClick={
                                        handleCheckAndUnCheckColumn
                                      }
                                      columnName={variable?.name}
                                      checkboxStyle={classes.customCheckbox}
                                      selectedIconStyle={
                                        classes.customRemoveIcon
                                      }
                                    />
                                  </div>
                                )}
                              </Draggable>
                            ))}

                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}

                {(!workflowDataVariables?.data?.[0]?.variables?.length ||
                  !workflowDataVariables?.data?.[0]?.variables?.some(
                    (formItems) =>
                      formItemsVariableGroup.includes(formItems.info.group)
                  ) ||
                  isWorkflowDataVariablesError ||
                  isFetchingWorkflowDataVariables) && (
                  <NoWorkflowConfigured
                    isLoading={isFetchingWorkflowDataVariables}
                    isError={isWorkflowDataVariablesError}
                  />
                )}
              </div>
            </>
          )}
        </>
      }
    >
      {/* table section */}
      <div>
        {/* Unfilled/In-active state when there's no data */}
        {isFetchingWorkflowDataVariables ||
        !selectedFormColumns?.length ||
        isWorkflowDataVariablesError ? (
          <EmptyStateCreateDataTable
            onAddColumn={() => setAddColumns(true)}
            isLoading={isFetchingWorkflowDataVariables}
            isError={isWorkflowDataVariablesError}
          />
        ) : (
          <>
            <div
              style={{
                maxHeight: "70vh",
                maxWidth: "100%",
                overflow: "auto",
              }}
            >
              <TableContainer
                component={Paper}
                elevation={0}
                style={{
                  minWidth: "600px",
                  overflowX: "auto",
                }}
              >
                <Table
                  classes={{ root: classes.table }}
                  aria-label="create-data-table"
                >
                  <TableHead classes={{ root: classes.tableHead }}>
                    <TableRow>
                      {selectedFormColumns
                        ?.map((selectedform) => {
                          const matchingItem =
                            workflowDataVariables?.data?.[0]?.variables?.find(
                              (workflowItem) =>
                                workflowItem.id === selectedform.id
                            );

                          if (matchingItem) {
                            return {
                              id: matchingItem.id, // Or matchingItem.id, they should be the same
                              name: matchingItem.info.name,
                            };
                          }
                          return {};
                        })
                        ?.map((item) => {
                          return (
                            <StyledTableCell align="left">
                              <TableHeaderCellName text={item?.name} />
                            </StyledTableCell>
                          );
                        })}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {Array.from({
                      length: placeholderPaginationLength,
                    })?.map((_, rowIndex) => {
                      return (
                        <TableRow key={rowIndex}>
                          {placeholderRowItemCell?.map((item, colIndex) => {
                            return (
                              <TableCell
                                style={{ minWidth: 150 }}
                                key={colIndex}
                                component="th"
                                scope="row"
                                classes={{ root: classes.tableCell }}
                              >
                                <Typography
                                  noWrap={true}
                                  style={{
                                    padding: "8px",
                                    fontFamily: "Avenir,Inter",
                                    fontSize: "14px",
                                    color: "#858585",
                                    paddingLeft: 0,
                                  }}
                                >
                                  {item.name}
                                </Typography>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingTop: "15px",
              }}
            >
              <Button
                classes={{ root: classes.saveButton }}
                variant="contained"
                aria-label="save-button"
                size="large"
                disabled={saveButtonRef.current}
                onClick={handleSaveCreateDataTable}
              >
                {isSavingAppSummaryConfiguration ? "Saving..." : "Save"}
              </Button>

              <div className={classes.reuseDisplayFlex}>
                <Typography
                  style={{
                    fontFamily: " Avenir",
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "#858585",
                  }}
                >
                  1 of {placeholderPaginationLength}
                </Typography>
                <div className={classes.reuseDisplayFlex}>
                  <div>
                    <KeyboardArrowLeftRoundedIcon
                      fontSize="large"
                      classes={{ root: classes.arrowPagination }}
                    />
                  </div>
                  <div>
                    <KeyboardArrowRightRoundedIcon
                      fontSize="large"
                      classes={{ root: classes.arrowPagination }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </DataTableDialogWrapper>
  );
};

export default CreateDataTable;
